import { Suspense, useState } from "react";
import { ErrorBoundary } from "@suspensive/react";
import { SuspenseQuery } from "@suspensive/react-query";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ArrowDown, ArrowUp, Plus, Save, Trash2 } from "lucide-react";
import { SectionErrorFallback } from "@/components/error-boundary/error-fallback";
import { TableLoadingFallback } from "@/components/suspense/loading-fallback";
import { structureQueryOptions, useUpdateStructure } from "@/hooks/use-structure";
import type {
  StructureGroup,
  StructureMember,
  UpdateStructureInput,
} from "@/types/structure";

export default function StructurePage() {
  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">분류 체계 관리</h1>
        <p className="text-muted-foreground leading-normal">
          위키 분류(존재들/원리/의례)의 구성을 편집합니다. 존재들은 태그로, 원리·의례는
          문서(slug)로 멤버를 지정합니다.
        </p>
      </div>

      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary onReset={reset} fallback={SectionErrorFallback}>
            <Suspense fallback={<TableLoadingFallback columnCount={1} />}>
              <SuspenseQuery {...structureQueryOptions()}>
                {({ data }) => <StructureEditor initial={data.groups} />}
              </SuspenseQuery>
            </Suspense>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
 * 편집기 — 로드한 그룹을 로컬 state 로 복제해 편집, 저장 시 뮤테이션.
 * ───────────────────────────────────────────────────── */
function StructureEditor({ initial }: { initial: StructureGroup[] }) {
  const [groups, setGroups] = useState<StructureGroup[]>(() =>
    structuredClone(initial),
  );
  const update = useUpdateStructure();

  function patchGroup(idx: number, patch: Partial<StructureGroup>) {
    setGroups((prev) =>
      prev.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    );
  }

  function setMembers(idx: number, members: StructureMember[]) {
    patchGroup(idx, { members });
  }

  function onSave() {
    const input: UpdateStructureInput = {
      groups: groups.map((g) => ({
        key: g.key,
        title: g.title.trim(),
        subtitle: g.subtitle?.trim() || undefined,
        description: g.description?.trim() || undefined,
        memberKind: g.memberKind,
        members: g.members
          .map((m) =>
            g.memberKind === "tag"
              ? {
                  kind: "tag" as const,
                  tag: (m.tag ?? "").trim(),
                  label: m.label?.trim() || undefined,
                  badgeLabel: m.badgeLabel?.trim() || undefined,
                }
              : {
                  kind: "doc" as const,
                  slug: (m.slug ?? "").trim(),
                  label: m.label?.trim() || undefined,
                },
          )
          // 빈 멤버(태그/slug 미입력) 제외.
          .filter((m) =>
            m.kind === "tag" ? Boolean(m.tag) : Boolean(m.slug),
          ),
      })),
    };
    update.mutate(input);
  }

  return (
    <div className="space-y-4">
      {groups.map((group, idx) => (
        <GroupCard
          key={group.key}
          group={group}
          onMeta={(patch) => patchGroup(idx, patch)}
          onMembers={(members) => setMembers(idx, members)}
        />
      ))}

      <div className="flex justify-end">
        <Button onClick={onSave} disabled={update.isPending}>
          <Save className="mr-2 h-4 w-4" />
          {update.isPending ? "저장 중..." : "저장"}
        </Button>
      </div>
    </div>
  );
}

function GroupCard({
  group,
  onMeta,
  onMembers,
}: {
  group: StructureGroup;
  onMeta: (patch: Partial<StructureGroup>) => void;
  onMembers: (members: StructureMember[]) => void;
}) {
  const isTag = group.memberKind === "tag";

  function addMember() {
    onMembers([
      ...group.members,
      isTag ? { kind: "tag", tag: "" } : { kind: "doc", slug: "" },
    ]);
  }
  function patchMember(i: number, patch: Partial<StructureMember>) {
    onMembers(group.members.map((m, j) => (j === i ? { ...m, ...patch } : m)));
  }
  function removeMember(i: number) {
    onMembers(group.members.filter((_, j) => j !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= group.members.length) return;
    const next = [...group.members];
    [next[i], next[j]] = [next[j], next[i]];
    onMembers(next);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {group.title || group.key}
          <span className="rounded bg-muted px-2 py-0.5 text-xs font-normal text-muted-foreground">
            {isTag ? "태그형" : "문서형"} · {group.path}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 px-4 md:px-6">
        {/* 그룹 메타 */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>제목</Label>
            <Input
              value={group.title}
              onChange={(e) => onMeta({ title: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>부제</Label>
            <Input
              value={group.subtitle}
              onChange={(e) => onMeta({ subtitle: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>설명</Label>
          <Textarea
            rows={2}
            value={group.description}
            onChange={(e) => onMeta({ description: e.target.value })}
          />
        </div>

        <Separator />

        {/* 멤버 */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{isTag ? "태그 멤버" : "문서 멤버 (slug)"}</Label>
            <Button variant="outline" size="sm" onClick={addMember}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              멤버 추가
            </Button>
          </div>

          {group.members.length === 0 && (
            <p className="text-sm text-muted-foreground">
              멤버가 없습니다. "멤버 추가"로 시작하세요.
            </p>
          )}

          <div className="space-y-2">
            {group.members.map((m, i) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-md border p-2"
              >
                <div className="grid flex-1 gap-2 sm:grid-cols-3">
                  {isTag ? (
                    <>
                      <Input
                        placeholder="태그 (예: 귀신)"
                        value={m.tag ?? ""}
                        onChange={(e) => patchMember(i, { tag: e.target.value })}
                      />
                      <Input
                        placeholder="표시 라벨 (선택)"
                        value={m.label ?? ""}
                        onChange={(e) =>
                          patchMember(i, { label: e.target.value })
                        }
                      />
                      <Input
                        placeholder="배지 라벨 (선택)"
                        value={m.badgeLabel ?? ""}
                        onChange={(e) =>
                          patchMember(i, { badgeLabel: e.target.value })
                        }
                      />
                    </>
                  ) : (
                    <>
                      <Input
                        className="sm:col-span-2"
                        placeholder="문서 slug (예: byeongnip-singwan)"
                        value={m.slug ?? ""}
                        onChange={(e) =>
                          patchMember(i, { slug: e.target.value })
                        }
                      />
                      <Input
                        placeholder="표시 라벨 (선택)"
                        value={m.label ?? ""}
                        onChange={(e) =>
                          patchMember(i, { label: e.target.value })
                        }
                      />
                    </>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label="위로"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => move(i, 1)}
                    disabled={i === group.members.length - 1}
                    aria-label="아래로"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={() => removeMember(i)}
                    aria-label="삭제"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
